import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Plus, Search, Ticket as TicketIcon, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PRIORITY, TICKET_STATUS, initials, timeAgo, Dot } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";
import CreateTicketDialog from "@/components/CreateTicketDialog";
import TicketDrawer from "@/components/TicketDrawer";

export default function TicketsPage() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [clients, setClients] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [active, setActive] = useState(null);

  const load = async () => {
    const { data } = await api.get("/tickets");
    setTickets(data);
  };
  useEffect(() => {
    load();
    api.get("/users", { params: { role: "client" } }).then((r) => setClients(r.data)).catch(() => {});
    api.get("/users", { params: { role: "engineer" } }).then((r) => setEngineers(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => tickets.filter((t) =>
    (status === "all" || t.status === status) &&
    (!q || (t.title + " " + t.key + " " + (t.description || "")).toLowerCase().includes(q.toLowerCase()))
  ), [tickets, status, q]);

  const counts = useMemo(() => {
    const c = { all: tickets.length, open: 0, in_progress: 0, resolved: 0, closed: 0 };
    tickets.forEach((t) => { c[t.status] = (c[t.status] || 0) + 1; });
    return c;
  }, [tickets]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1300px]" data-testid="tickets-page">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Bugs &amp; Tickets</h1>
          <p className="text-sm text-slate-500 mt-1">Track issues raised by clients and the team through to resolution.</p>
        </div>
        <Button onClick={() => setCreating(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-ticket-button">
          <Plus className="w-4 h-4 mr-1.5" /> New ticket
        </Button>
      </div>

      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <Tabs value={status} onValueChange={setStatus} className="max-w-full overflow-x-auto scroll-thin">
          <TabsList>
            <TabsTrigger value="all" data-testid="ticket-filter-all">All ({counts.all})</TabsTrigger>
            <TabsTrigger value="open">Open ({counts.open})</TabsTrigger>
            <TabsTrigger value="in_progress">In Progress ({counts.in_progress})</TabsTrigger>
            <TabsTrigger value="resolved">Resolved ({counts.resolved})</TabsTrigger>
            <TabsTrigger value="closed">Closed ({counts.closed})</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative w-full sm:w-64">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tickets…" className="pl-9 h-9" data-testid="ticket-search-input" />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden">
        {filtered.map((t) => (
          <button key={t.id} onClick={() => setActive(t.id)} data-testid={`ticket-row-${t.id}`}
            className="w-full text-left px-4 py-3.5 hover:bg-slate-50 transition-colors flex items-center gap-4">
            <Dot className={PRIORITY[t.priority]?.dot} />
            <span className="font-mono text-xs font-semibold text-slate-500 w-20 shrink-0">{t.key}</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{t.title}</p>
              <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-400 flex-wrap">
                <span>{t.client_name || "Internal"}</span>
                {t.task_title && <span>· linked: {t.task_title}</span>}
                <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{t.age_days}d old</span>
              </div>
            </div>
            <Badge variant="outline" className={`h-5 px-1.5 text-[10px] shrink-0 ${TICKET_STATUS[t.status]?.chip}`}>{TICKET_STATUS[t.status]?.label}</Badge>
            {t.assignee_name && (
              <Avatar className="w-7 h-7 shrink-0"><AvatarImage src={t.assignee_avatar} /><AvatarFallback className="bg-slate-100 text-slate-500 text-[10px]">{initials(t.assignee_name)}</AvatarFallback></Avatar>
            )}
          </button>
        ))}
        {filtered.length === 0 && (
          <div className="py-16 text-center text-slate-400">
            <TicketIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
            No tickets match this view.
          </div>
        )}
      </div>

      {creating && (
        <CreateTicketDialog isClient={user?.role === "client"} clients={clients} engineers={engineers}
          onClose={() => setCreating(false)} onCreated={load} />
      )}
      {active && <TicketDrawer ticketId={active} engineers={engineers} onClose={() => setActive(null)} onChanged={load} />}
    </div>
  );
}
